import type { ReactNode } from 'react';

/**
 * Masked line reveal — the "headline rises from behind a clip mask" move.
 *
 * Routes through the GLOBAL `.lines / .line` choreography already defined in
 * motion.css (dormant until now). Those are plain global classNames, so we write
 * them as strings here; a *.module.css could not reference the bare keyframe
 * names (see the keyframe-scoping note at the top of motion.css).
 *
 * Pass `inView` to self-arm (toggles the global `in` class). Each `lines` block
 * masks one or more child lines: pass a single node for a one-line rise, or an
 * array for a staggered multi-line cascade (the per-line delay comes from the
 * `.line:nth-child(n)` rules in motion.css). Composes harmlessly with a parent's
 * own opacity fade. Reduced motion: the global media query zeroes the transition,
 * so the text simply snaps into place — never trapped behind the mask.
 */
export function MaskedReveal({
  children,
  inView,
  className,
}: {
  children: ReactNode | ReactNode[];
  inView?: boolean;
  className?: string;
}) {
  const lines = Array.isArray(children) ? children : [children];
  return (
    <span className={['lines', inView ? 'in' : '', className].filter(Boolean).join(' ')}>
      {lines.map((line, i) => (
        <span className="line" key={i}>
          <span>{line}</span>
        </span>
      ))}
    </span>
  );
}
